import get from 'lodash/get'
import { Children, type MouseEvent, type ReactNode } from 'react'
import { Link, type RaRecord, RecordContextProvider, useRecordContext } from 'react-admin'

interface LinkToRecordProps {
  /** Defaults to the record in context — a Datagrid row, a Show page. */
  record?: Partial<RaRecord> & Record<string, any>
  resource: string
  /** The field holding the linked record's id. */
  id?: string
  text?: ReactNode
  linkType?: 'show' | 'edit' | false
  children?: ReactNode
  childrenSeparator?: ReactNode
  disabled?: boolean
}

export const LinkToRecord = ({
  record: recordProp,
  resource,
  id = 'id',
  text,
  linkType = 'show',
  children,
  childrenSeparator = ' ',
  disabled = false,
}: LinkToRecordProps) => {
  // react-admin 5 no longer hands a Datagrid row's record to its cells as a
  // prop; without the context, every customer and boat page that listed a
  // relationship crashed on `undefined.related_customer_id`.
  const contextRecord = useRecordContext()
  const record = recordProp ?? contextRecord
  if (!record) return null

  // linkToRecord was removed in react-admin 4; the route is just the id,
  // with `show`/`edit` appended when a link type is given.
  const recordId = get(record, id)
  const linkToUser =
    linkType && linkType !== 'edit'
      ? `/${resource}/${recordId}/${linkType}`
      : `/${resource}/${recordId}`
  const label = children ? (
    <RecordContextProvider value={record}>
      {Children.map(children, (child, index) => (
        <span>
          {!!index && childrenSeparator}
          {child}
        </span>
      ))}
    </RecordContextProvider>
  ) : (
    (text ?? String(recordId ?? ''))
  )

  return disabled ? (
    <span>{label}</span>
  ) : (
    // Inside a clickable row, the link is the click: the row must not also act.
    <Link to={linkToUser} onClick={(e: MouseEvent) => e.stopPropagation()}>
      {label}
    </Link>
  )
}

export default LinkToRecord
